/* eslint-disable camelcase */
/* eslint-disable func-style */
import React, { useCallback, useReducer, Fragment } from 'react';
import { Message, Table, Grid } from 'semantic-ui-react';
// eslint-disable-next-line import/no-named-as-default
import styled from 'styled-components';
import useDateMoment from './useDateMoment.jsx';
import ActionButton from './ActionButton.jsx';

const FIELD_DESCRIPTION =
  'This control will enable the Edge Install Monitoring URL and the ability to generate new URLs. ' +
  'Please note, when generating new URLs previously URLs will no longer be valid.';

const StyledActionButton = styled(ActionButton)`
  color: ${(props) => (props.alert ? '#9F2424' : '#3066A1')}!important;
  background: ${(props) => (props.alert ? '#FFDDDD' : '#C4E0F1')}!important;

  .io-form:not(.disabled-form) & {
    display: inline-block;
  }
`;

const GridSized = styled(Grid)`
  max-width: 500px;

  // TODO: Remove padding declarations when we update semantic-ui to fomantic-ui: use fomantic-ui compact grid instead
  &&&& > * {
    &:nth-child(-n + 2) {
      padding: 0 0 0.2rem;
    }

    &:nth-child(n + 3):not(:last-child) {
      padding: 0 0 1rem;
    }

    &:last-child {
      padding: 0;
    }
  }
`;

const tokenPropsExtractor = (tokenProps = {}) => {
  const { info, error, enabled, expired, ...remaining } = tokenProps;

  return {
    enabled,
    active: enabled && !expired,
    feedbackMessage: error || info || '',
    hasError: !!error,
    ...remaining,
  };
};

const tokenReducer = (state, action) => {
  switch (action.type) {
    case 'TOGGLE_TOKEN':
      return { ...state, ...tokenPropsExtractor(action.payload) };
    case 'SET_NEW_TOKEN':
      return tokenPropsExtractor(action.payload);
    default:
      throw new Error(`Action ${action.type} is not supported`);
  }
};

export default function TokenForm({ token: initialToken, generateTokenPath }) {
  const [state, dispatch] = useReducer(
    tokenReducer,
    initialToken,
    tokenPropsExtractor,
  );

  const handleGenerateToken = useCallback((response) => {
    dispatch({ type: 'SET_NEW_TOKEN', payload: response });
  }, []);

  const handleToggleToken = useCallback((response) => {
    dispatch({ type: 'TOGGLE_TOKEN', payload: response });
  }, []);

  const {
    uuid,
    enabled,
    active,
    accessed_by,
    expires_at,
    accessed_at,
    feedbackMessage,
    hasError,
  } = state;
  const uuidURL = `/${currentCustomer}/edge_install/${uuid}`;
  const urlStatus = active ? (
    <a
      href={uuidURL}
      rel='noreferrer'
      target='_blank'
    >
      enabled
    </a>
  ) : (
    <strong>disabled</strong>
  );

  return (
    <FormFieldRow
      description={FIELD_DESCRIPTION}
      label='Edge Install Monitoring'
    >
      {feedbackMessage && (
        <Message
          className={hasError ? 'error' : 'info'}
          content={feedbackMessage}
          size='tiny'
        />
      )}

      <GridSized
        padded
        stackable
      >
        <Grid.Column width={11}>Installation URL is {urlStatus}.</Grid.Column>

        <Grid.Column
          textAlign='right'
          width={5}
        >
          <StyledActionButton
            askConfirmation
            className='hide-disabled'
            confirmationMessage={`Please confirm ${active ? 'deactivation' : 'activation'} of the Edge Monitor Installation URL.`}
            floated='right'
            path={`${uuidURL}/${active ? 'disable' : 'enable'}`}
            onChange={handleToggleToken}
          >
            {active ? 'Disable' : 'Enable'}
          </StyledActionButton>
        </Grid.Column>

        {enabled && (
          <Grid.Column width={16}>
            <DetailsContainer
              accessedAt={accessed_at}
              accessedBy={accessed_by}
              expiresAt={expires_at}
            />
          </Grid.Column>
        )}

        <Grid.Column width={16}>
          <StyledActionButton
            alert
            askConfirmation
            className='hide-disabled'
            confirmationMessage='Please confirm generation of new Edge Monitor Installation URL, note that any previous URLs will no longer be valid.'
            path={generateTokenPath}
            requestMethod='POST'
            onChange={handleGenerateToken}
          >
            Generate URL
          </StyledActionButton>
        </Grid.Column>
      </GridSized>
    </FormFieldRow>
  );
}

function FormFieldRow({ label, description, children }) {
  return (
    <Table.Row className='even'>
      <Table.Cell className='name'>
        <label>{label}:</label>
        <p className='help'>{description}</p>
      </Table.Cell>

      <Table.Cell>{children}</Table.Cell>
    </Table.Row>
  );
}

function DetailsContainer({ accessedBy, expiresAt, accessedAt }) {
  const [expireDate, formattedExpireDate] = useDateMoment(expiresAt);
  const [, formattedAccessDate] = useDateMoment(accessedAt);

  if (!accessedBy && !expiresAt) {
    return 'URL is active, but not yet accessed.';
  }

  return (
    <Fragment>
      {expiresAt && (
        <Fragment>
          {expireDate.fromNow()}
          <br />
          {expireDate.isBefore() ? 'Expired' : 'Expires'} at{' '}
          {formattedExpireDate} <br />
        </Fragment>
      )}
      {accessedBy &&
        `Last accessed by this IP Address: ${accessedBy} (${formattedAccessDate})`}
    </Fragment>
  );
}
